"use client";

import { useEffect, useRef } from "react";
import { useIsMobile } from "./_lib/useMediaQuery";

const PARALLAX_LERP = 0.045;
const MAX_DPR = 1.5;

interface LayerConfig {
  starCount: number;
  sizeMin: number;
  sizeMax: number;
  opacityMin: number;
  opacityMax: number;
  rotationSpeed: number;
  parallaxStrength: number;
  shadowBlur: number;
  centerX: number;
  centerY: number;
  direction: 1 | -1;
  startAngle: number;
}

const LAYER_DEFS: LayerConfig[] = [
  { starCount: 55, sizeMin: 0.4, sizeMax: 0.9, opacityMin: 0.12, opacityMax: 0.3, rotationSpeed: 0.00018, parallaxStrength: 18, shadowBlur: 3, centerX: 0.65, centerY: 0.10, direction: -1, startAngle: 1.2 },
  { starCount: 49, sizeMin: 0.5, sizeMax: 1.2, opacityMin: 0.18, opacityMax: 0.4, rotationSpeed: 0.0004, parallaxStrength: 32, shadowBlur: 1.6, centerX: 0.40, centerY: 0.35, direction: 1, startAngle: 2.8 },
  { starCount: 39, sizeMin: 0.6, sizeMax: 1.5, opacityMin: 0.3, opacityMax: 0.6, rotationSpeed: 0.0008, parallaxStrength: 52, shadowBlur: 0, centerX: 0.55, centerY: 0.55, direction: -1, startAngle: 4.1 },
  { starCount: 27, sizeMin: 0.8, sizeMax: 2.0, opacityMin: 0.4, opacityMax: 0.85, rotationSpeed: 0.0014, parallaxStrength: 80, shadowBlur: 0, centerX: 0.30, centerY: 0.45, direction: 1, startAngle: 5.5 },
];

function scaleLayerCounts(mobile: boolean): LayerConfig[] {
  const factor = mobile ? 0.75 : 1;
  return LAYER_DEFS.map((cfg) => ({
    ...cfg,
    starCount: Math.max(20, Math.round(cfg.starCount * factor)),
  }));
}

type Star = {
  originX: number;
  originY: number;
  x: number;
  y: number;
  r: number;
  opacity: number;
  angle: number;
  dist: number;
};

type LayerState = {
  cfg: LayerConfig;
  stars: Star[];
  rotCenter: { x: number; y: number };
  rotAngle: number;
  smoothOffset: { x: number; y: number };
};

function initLayerStars(
  cfg: LayerConfig,
  width: number,
  height: number
): LayerState {
  const rotCenter = { x: width * cfg.centerX, y: height * cfg.centerY };

  const stars: Star[] = Array.from({ length: cfg.starCount }, () => {
    const ox = Math.random() * width;
    const oy = Math.random() * height;
    const dx = ox - rotCenter.x;
    const dy = oy - rotCenter.y;
    return {
      originX: ox,
      originY: oy,
      x: ox,
      y: oy,
      r: cfg.sizeMin + Math.random() * (cfg.sizeMax - cfg.sizeMin),
      opacity: cfg.opacityMin + Math.random() * (cfg.opacityMax - cfg.opacityMin),
      angle: Math.atan2(dy, dx),
      dist: Math.sqrt(dx * dx + dy * dy),
    };
  });

  return {
    cfg,
    stars,
    rotCenter,
    rotAngle: cfg.startAngle,
    smoothOffset: { x: 0, y: 0 },
  };
}

function stepLayer(layer: LayerState, mouse: { x: number; y: number }, dpr: number) {
  const { cfg } = layer;
  layer.rotAngle += cfg.rotationSpeed * cfg.direction;

  const targetX = mouse.x * cfg.parallaxStrength * dpr;
  const targetY = mouse.y * cfg.parallaxStrength * dpr;
  layer.smoothOffset.x += (targetX - layer.smoothOffset.x) * PARALLAX_LERP;
  layer.smoothOffset.y += (targetY - layer.smoothOffset.y) * PARALLAX_LERP;

  for (const star of layer.stars) {
    const a = star.angle + layer.rotAngle;
    star.x = layer.rotCenter.x + Math.cos(a) * star.dist + layer.smoothOffset.x;
    star.y = layer.rotCenter.y + Math.sin(a) * star.dist + layer.smoothOffset.y;
  }
}

function drawLayer(
  ctx: CanvasRenderingContext2D,
  layer: LayerState,
  dpr: number
) {
  const blur = layer.cfg.shadowBlur * dpr;

  if (blur > 0) {
    ctx.save();
    ctx.shadowBlur = blur;
    ctx.shadowColor = "rgba(255, 255, 255, 0.85)";
  }

  for (const star of layer.stars) {
    ctx.beginPath();
    ctx.arc(star.x, star.y, star.r * dpr, 0, Math.PI * 2);
    ctx.fillStyle = `rgba(255, 255, 255, ${star.opacity})`;
    ctx.fill();
  }

  if (blur > 0) {
    ctx.restore();
  }
}

export default function StarField() {
  const rootRef = useRef<HTMLDivElement>(null);
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const isMobile = useIsMobile();

  useEffect(() => {
    const root = rootRef.current;
    const canvas = canvasRef.current;
    if (!root || !canvas) return;

    const ctx = canvas.getContext("2d", { alpha: true });
    if (!ctx) return;

    let animationId = 0;
    let layers: LayerState[] = scaleLayerCounts(isMobile).map((cfg) =>
      initLayerStars(cfg, 1, 1)
    );
    let dpr = Math.min(window.devicePixelRatio, MAX_DPR);
    let visible = true;

    const mouse = { x: 0, y: 0 };

    function resize() {
      dpr = Math.min(window.devicePixelRatio, MAX_DPR);
      const w = canvas!.offsetWidth;
      const h = canvas!.offsetHeight;
      const pixelW = w * dpr;
      const pixelH = h * dpr;

      canvas!.width = pixelW;
      canvas!.height = pixelH;

      layers = scaleLayerCounts(isMobile).map((cfg) =>
        initLayerStars(cfg, pixelW, pixelH)
      );
    }

    function onMouseMove(e: MouseEvent) {
      mouse.x = (e.clientX / window.innerWidth - 0.5) * 2;
      mouse.y = (e.clientY / window.innerHeight - 0.5) * 2;
    }

    function onTouchMove(e: TouchEvent) {
      if (e.touches.length === 0) return;
      const t = e.touches[0];
      mouse.x = (t.clientX / window.innerWidth - 0.5) * 2;
      mouse.y = (t.clientY / window.innerHeight - 0.5) * 2;
    }

    function onPointerLeaveWindow() {
      mouse.x = 0;
      mouse.y = 0;
    }

    function draw() {
      if (!visible) {
        animationId = 0;
        return;
      }

      ctx!.clearRect(0, 0, canvas!.width, canvas!.height);

      for (const layer of layers) {
        stepLayer(layer, mouse, dpr);
        drawLayer(ctx!, layer, dpr);
      }

      animationId = requestAnimationFrame(draw);
    }

    function startLoop() {
      if (animationId) return;
      animationId = requestAnimationFrame(draw);
    }

    function stopLoop() {
      cancelAnimationFrame(animationId);
      animationId = 0;
    }

    const visibilityObserver = new IntersectionObserver(
      ([entry]) => {
        visible = entry.isIntersecting;
        if (visible) {
          startLoop();
        } else {
          stopLoop();
        }
      },
      { root: null, threshold: 0 }
    );
    visibilityObserver.observe(root);

    resize();
    startLoop();

    window.addEventListener("resize", resize);
    window.addEventListener("mousemove", onMouseMove, { passive: true });
    window.addEventListener("touchmove", onTouchMove, { passive: true });
    document.documentElement.addEventListener("mouseleave", onPointerLeaveWindow);

    return () => {
      stopLoop();
      visibilityObserver.disconnect();
      window.removeEventListener("resize", resize);
      window.removeEventListener("mousemove", onMouseMove);
      window.removeEventListener("touchmove", onTouchMove);
      document.documentElement.removeEventListener("mouseleave", onPointerLeaveWindow);
    };
  }, [isMobile]);

  return (
    <div ref={rootRef} className="absolute inset-0">
      <canvas
        ref={canvasRef}
        className="pointer-events-none absolute inset-0 h-full w-full"
      />
    </div>
  );
}
